import type { ContainerLineStop } from "../types/containerOrders";
import { dayOf, timeOf } from "./routeSequence";

/**
 * Expected against actual, for one stop of a container's run.
 *
 * The container timeline's counterpart to the shipment timeline's schedule
 * badges, and it answers the same question: was the box where it was booked to
 * be, when it was booked to be there. A stop carries one appointment — its
 * `date` + `time` — and the appointment is kept when the *work at the stop is
 * done*: collected, loaded, returned. So this measures the stop's completion
 * stamp against it, not its arrival stamp. Turning up at the gate at 17:44 and
 * leaving with the box at 21:00 is not a slot kept, and measuring the arrival
 * said it was.
 *
 * The appointment is measured at whatever precision it was booked at, and the
 * date is the half that matters — a stop with a date and no time is still a
 * commitment, just to a day rather than to a moment:
 *
 *  · date + time — measured to the minute. Cleared at 21:00 against a 17:45
 *    slot is "+3h 15m late".
 *  · date only   — measured to the day. Cleared on the booked day is on time
 *    whatever the clock said; cleared two days later is "+2d late". The
 *    missing time is never read as midnight, which would report every such
 *    stop as hours late — a KPI invented out of the absence of data.
 *  · no date     — nothing to measure. Those steps keep the plain
 *    "recorded / pending" rendering, because there is no commitment to miss.
 *
 * The two precisions are never mixed into one number: a day-precision stop
 * reports whole days, and the timeline prints its planned side as a date with
 * no clock, so nobody reads a minute out of a booking that never carried one.
 */

/** Minutes either side of a to-the-minute appointment that count as on time. */
export const SCHEDULE_TOLERANCE_MINUTES = 5;

export type ScheduleTone = "early" | "onTime" | "late" | "atRisk" | "onTrack";

/** How finely the stop was booked, and so how finely it can be judged. */
export type SchedulePrecision = "minute" | "day";

export interface StopScheduleMetric {
  /**
   * The appointment. A moment for a `minute` stop; the local start of the
   * booked day for a `day` one, where only the date part is meaningful.
   */
  plannedAt: Date;
  /** When the stop was actually cleared, or null while it is unrecorded. */
  actualAt: Date | null;
  precision: SchedulePrecision;
  tone: ScheduleTone;
  /**
   * Signed distance from the appointment: actual − planned once the step is
   * recorded, and how overdue it is right now while it is not. Null when there
   * is nothing to say — unrecorded and not yet due. The unit follows
   * `precision`, so it is never formatted at a resolution the booking did not
   * have.
   */
  delta: { unit: "minutes" | "days"; value: number } | null;
}

/**
 * The appointment, and how finely it was made — or null when the stop carries
 * no date at all.
 *
 * The day is taken off the stored ISO string rather than off a parsed `Date`,
 * matching `dayOf` and the rest of the route rules: a date stored as UTC
 * midnight becomes the previous day in any western timezone once parsed
 * locally, which would move the whole appointment a day back. The clock is a
 * wall-clock time somebody typed at the stop, so the pair is read locally —
 * the same frame the timeline already prints stamps in.
 *
 * A time that is present but not a readable `hh:mm` falls back to the day
 * rather than throwing the whole appointment away: the date is still a real
 * commitment, and it is the half the office actually books to.
 */
export function plannedSlotOf(
  stop?: ContainerLineStop | null,
): { at: Date; precision: SchedulePrecision } | null {
  const day = dayOf(stop?.date);
  const dayMatch = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  if (!dayMatch) return null;

  const [, y, m, d] = dayMatch;
  const year = Number(y);
  const month = Number(m) - 1;
  const date = Number(d);

  const clockMatch = /^(\d{1,2}):(\d{2})$/.exec(timeOf(stop?.time) ?? "");
  const hours = clockMatch ? Number(clockMatch[1]) : 0;
  const minutes = clockMatch ? Number(clockMatch[2]) : 0;
  const clocked = Boolean(clockMatch) && hours <= 23 && minutes <= 59;

  const at = clocked
    ? new Date(year, month, date, hours, minutes)
    : new Date(year, month, date);
  if (Number.isNaN(at.getTime())) return null;

  // A day whose fields do not survive the round trip was never a real date —
  // "2026-02-31" would land in March and measure against the wrong day.
  if (
    at.getFullYear() !== year ||
    at.getMonth() !== month ||
    at.getDate() !== date
  )
    return null;

  return { at, precision: clocked ? "minute" : "day" };
}

const parseStamp = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Local midnight of whatever day a moment falls on. */
const startOfDay = (value: Date): Date =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate());

/**
 * Whole calendar days between two moments.
 *
 * Both ends are dropped to local midnight first, so this counts days as the
 * calendar counts them — 23:59 and the following 00:01 are one day apart, not
 * zero. The rounding absorbs the hour a DST changeover adds or removes between
 * the two midnights; without it that hour would shear the quotient off by one.
 */
const daysBetween = (from: Date, to: Date): number =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86400000);

/**
 * @param stop     the stop whose appointment is being measured
 * @param actualRaw the completion stamp, when the step has been recorded
 * @param options.forecast whether an unrecorded step may still be flagged as
 *   running late. False for a step the container has already moved past
 *   without a stamp (missing data, not a delay) and for a cancelled container,
 *   which is not late for an appointment it is no longer keeping.
 */
export function stopScheduleMetric(
  stop: ContainerLineStop | null | undefined,
  actualRaw: string | null | undefined,
  options: { forecast?: boolean; now?: Date } = {},
): StopScheduleMetric | null {
  const slot = plannedSlotOf(stop);
  if (!slot) return null;

  const { at: plannedAt, precision } = slot;
  const byDay = precision === "day";
  const actualAt = parseStamp(actualRaw);
  const base = { plannedAt, precision };

  if (actualAt) {
    // Days for a date-only booking, minutes for a booked moment. The tolerance
    // belongs to the minute case only: a day-precision stop cleared on its
    // booked day is on time by definition, and one cleared the next day is a
    // whole day late however few minutes past midnight it was.
    const value = byDay
      ? daysBetween(plannedAt, actualAt)
      : Math.round((actualAt.getTime() - plannedAt.getTime()) / 60000);
    const tolerance = byDay ? 0 : SCHEDULE_TOLERANCE_MINUTES;
    const tone: ScheduleTone =
      value > tolerance ? "late" : value < -tolerance ? "early" : "onTime";
    return {
      ...base,
      actualAt,
      tone,
      delta: { unit: byDay ? "days" : "minutes", value },
    };
  }

  // Not cleared. Only worth flagging once the appointment has actually passed,
  // and no tolerance here — the booking is already missed, so the number is how
  // late it is so far rather than how late it will turn out to be. A day
  // booking is not missed until the day itself is over, so an unrecorded stop
  // booked for today stays on track until midnight rather than going at risk
  // the moment the clock passes a start-of-day it was never booked for.
  const now = options.now ?? new Date();
  const overdue = byDay
    ? daysBetween(plannedAt, now)
    : Math.round((now.getTime() - plannedAt.getTime()) / 60000);
  if (options.forecast !== false && overdue > 0) {
    return {
      ...base,
      actualAt: null,
      tone: "atRisk",
      delta: { unit: byDay ? "days" : "minutes", value: overdue },
    };
  }

  return { ...base, actualAt: null, tone: "onTrack", delta: null };
}

/** Hours and minutes of a duration, for a "2h 15m" style label. */
export function splitDuration(minutes: number): { h: number; m: number } {
  const abs = Math.abs(Math.round(minutes));
  return { h: Math.floor(abs / 60), m: abs % 60 };
}
